import React from 'react';

const niceNumber = (range, round) => {
  const exponent = Math.floor(Math.log10(range));
  const fraction = range / Math.pow(10, exponent);
  const thresholds = round ? [1.5, 3, 7] : [1, 2, 5];
  const index = thresholds.findIndex(threshold => round ? fraction < threshold : fraction <= threshold);
  return [1, 2, 5, 10][index < 0 ? 3 : index] * Math.pow(10, exponent);
};

export default function JobAnalyticsChart({ data = [], metric = 'views', isRTL = false, locale = 'en', width = 440, height = 160, svgHeight = 180, label, emptyLabel }) {
  if (!data.length) return <p style={{ opacity: 0.8, color: 'var(--app-text, #FFFFFF)' }}>{emptyLabel}</p>;
  const values = data.map(month => Number(metric === 'views' ? month.views : month.applies) || 0);
  const niceMax = niceNumber(Math.max(1, ...values), false);
  const tickStep = Math.max(1, niceNumber(niceMax / 5, true));
  const axisMax = Math.ceil(niceMax / tickStep) * tickStep;
  const padX = 40;
  const padY = height === 180 ? 24 : 20;
  const axisX = isRTL ? width - padX : padX;
  const xFor = index => {
    const offset = index * (width - 2 * padX) / Math.max(1, data.length - 1);
    return isRTL ? width - padX - offset : padX + offset;
  };
  const yFor = value => height - padY - value / axisMax * (height - 2 * padY);
  const ticks = Array.from({ length: axisMax / tickStep + 1 }, (_, index) => index * tickStep);
  const numberFormat = new Intl.NumberFormat(locale);
  const monthFormat = new Intl.DateTimeFormat(locale, { month: 'short', timeZone: 'UTC' });
  const monthLabel = month => {
    if (!/^\d{4}-\d{2}$/.test(month.key || '')) return month.label;
    return monthFormat.format(new Date(`${month.key}-01T00:00:00Z`));
  };
  const ink = 'var(--app-text, #FFFFFF)';

  return (
    <div className="job-analytics-chart" style={{ width: '100%', overflowX: 'auto' }}>
      <svg viewBox={`0 0 ${width} ${height}`} width="100%" height={svgHeight} direction="ltr" role="img" aria-label={label}>
        <line data-axis="y" x1={axisX} y1={padY} x2={axisX} y2={height - padY} stroke={ink} opacity="0.3" />
        <line x1={padX} y1={height - padY} x2={width - padX} y2={height - padY} stroke={ink} opacity="0.3" />
        {ticks.map(value => (
          <text key={value} x={axisX + (isRTL ? 8 : -8)} y={yFor(value) + 4} fontSize="10" textAnchor={isRTL ? 'start' : 'end'} fill={ink} opacity="0.6" direction="ltr">{numberFormat.format(value)}</text>
        ))}
        {ticks.slice(1, -1).map(value => (
          <line key={value} x1={padX} y1={yFor(value)} x2={width - padX} y2={yFor(value)} stroke={ink} opacity="0.1" />
        ))}
        <polyline fill="none" stroke="#568ded" strokeWidth="2" points={values.map((value, index) => `${xFor(index)},${yFor(value)}`).join(' ')} />
        {values.map((value, index) => (
          <React.Fragment key={data[index].key || index}>
            <circle cx={xFor(index)} cy={yFor(value)} r="3" fill="#568DED" />
            <text data-label="value" x={xFor(index)} y={yFor(value) - 8} fontSize="10" textAnchor="middle" fill={ink} opacity="0.9" fontWeight="bold" direction="ltr">{numberFormat.format(value)}</text>
            <text data-label="month" x={xFor(index)} y={height - 4} fontSize="10" textAnchor="middle" fill={ink} opacity="0.8" direction={isRTL ? 'rtl' : 'ltr'}>{monthLabel(data[index])}</text>
          </React.Fragment>
        ))}
      </svg>
    </div>
  );
}
